import { ComponentProps } from "react";
import { FormattedMessage } from "react-intl";

import CardBase from "@components/cards/CardBase";
import styles from "@components/carePlan/CarePlanMenu.module.css";
import Trackers from "@components/carePlan/Trackers";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import { CarePlanSectionEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery } from "@typing/Generated";
import { Direction } from "@typing/Models";
import { IconName } from "@utils/iconUtils";
import { TextureName, textureUrl } from "@utils/imageUtils";

type Version = "desktop" | "mobile";
type CarePlanSectionTabs =
  | CarePlanSectionEnum.CARE_PLAN
  | CarePlanSectionEnum.CARE_TEAM
  | CarePlanSectionEnum.DRIVE
  | CarePlanSectionEnum.PROFILE
  | CarePlanSectionEnum.TRACKERS;

type BodyProps = Pick<ComponentProps<typeof Icon>, "icon"> & {
  section: CarePlanSectionEnum;
  version: Version;
};

const Body = ({ icon, section, version }: BodyProps) => {
  if (version === "desktop") {
    return (
      <>
        <div className="flex items-center gap-lg">
          <Heading2 className="bold flex-grow text-md" color="white">
            <FormattedMessage id={`pages.carePlan.sections.${section}.title`} />
          </Heading2>
          <Icon color="white" icon={icon} size="large" />
        </div>
        <BodyCopy bold color="white" size="small">
          <FormattedMessage id={`pages.carePlan.sections.${section}.description`} />
        </BodyCopy>
      </>
    );
  }

  return (
    <div className="flex flex-column full-height">
      <div className={`${styles.iconContainer} flex-grow`}>
        <Icon color="white" icon={icon} size="large" />
      </div>
      <div>
        <Heading2 className="bold text-md" color="white">
          <FormattedMessage id={`pages.carePlan.sections.${section}.title`} />
        </Heading2>
        <BodyCopy bold color="white" size="small">
          <FormattedMessage id={`pages.carePlan.sections.${section}.description`} />
        </BodyCopy>
      </div>
    </div>
  );
};

type Props = {
  currentSection: CarePlanSectionEnum | null;
  handleCurrentSectionChange: (section: CarePlanSectionEnum, direction: Direction) => void;
  journey: GetJourneyWithPlanQuery["journey"];
  version: Version;
};

const CarePlanMenu = ({ currentSection, handleCurrentSectionChange, journey, version }: Props) => {
  const isActive = (section: CarePlanSectionEnum) => currentSection === section;

  const sectionOrder: CarePlanSectionTabs[] = [
    CarePlanSectionEnum.CARE_PLAN,
    CarePlanSectionEnum.PROFILE,
    CarePlanSectionEnum.CARE_TEAM,
    CarePlanSectionEnum.DRIVE,
    CarePlanSectionEnum.TRACKERS
  ];

  type SectionData = {
    [key in CarePlanSectionTabs]: {
      activeColor: ComponentProps<typeof CardBase>["color"];
      icon: IconName;
      texture: TextureName;
    };
  };

  const sectionData: SectionData = {
    [CarePlanSectionEnum.CARE_PLAN]: {
      activeColor: "primary",
      icon: "check",
      texture: "purpleSplash"
    },
    [CarePlanSectionEnum.PROFILE]: {
      activeColor: "peach-900",
      icon: "user",
      texture: "peachSplash"
    },
    [CarePlanSectionEnum.CARE_TEAM]: {
      activeColor: "secondary-900",
      icon: "heartCirclePlus",
      texture: "tealSplash"
    },
    [CarePlanSectionEnum.DRIVE]: {
      activeColor: "pink-900",
      icon: "folder",
      texture: "pinkSplash"
    },
    [CarePlanSectionEnum.TRACKERS]: {
      activeColor: "primary-500",
      icon: "listUl",
      texture: "blueSplash"
    }
  };

  const mobileStylesForSection = (section: CarePlanSectionTabs): ComponentProps<typeof CardBase>["style"] => {
    const mobileStyles: ComponentProps<typeof CardBase>["style"] = {};
    if (version === "mobile") {
      mobileStyles["backgroundImage"] = `url(${textureUrl(sectionData[section].texture)})`;
      mobileStyles["backgroundPosition"] = "center center";
      mobileStyles["backgroundRepeat"] = "no-repeat";
      mobileStyles["backgroundSize"] = "cover";
    }
    return mobileStyles;
  };

  const isCarePlanSectionTabs = (section: CarePlanSectionEnum): section is CarePlanSectionTabs =>
    section === CarePlanSectionEnum.CARE_PLAN ||
    section === CarePlanSectionEnum.CARE_TEAM ||
    section === CarePlanSectionEnum.DRIVE ||
    section === CarePlanSectionEnum.PROFILE ||
    section === CarePlanSectionEnum.TRACKERS;

  if (currentSection && !isCarePlanSectionTabs(currentSection) && currentSection !== CarePlanSectionEnum.MENU)
    return null;

  return (
    <div className={version === "desktop" ? "ion-hide-lg-down" : "ion-hide-lg-up"} data-test="care-plan-menu">
      <div className={`flex gap-md margin-20-bottom ${styles.mobileMenu}`}>
        {sectionOrder.map(section => (
          <CardBase
            body={<Body icon={sectionData[section].icon} section={section} version={version} />}
            border={false}
            className={`${styles.cardPlanMenuCard} flex flex-column flex-equal ${section === CarePlanSectionEnum.TRACKERS ? "ion-hide-lg-down" : ""}`}
            color={isActive(section) ? sectionData[section].activeColor : "secondary"}
            contentClassName="full-height"
            disabled={isActive(section)}
            key={section}
            onClick={() => handleCurrentSectionChange(section, "right")}
            style={mobileStylesForSection(section)}
          />
        ))}
      </div>

      {version === "mobile" && <Trackers journey={journey} />}
    </div>
  );
};

export default CarePlanMenu;
